import React from 'react';

/** Shared geometry for both charts so axes line up when they sit side by side. */
const PAD = { top: 10, right: 6, bottom: 24, left: 30 };
const W = 320;
const H = 180;

const niceMax = (max: number) => {
  if (max <= 0) return 4;
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  return Math.ceil(max / step) * step;
};

interface BarDatum {
  label: string;
  value: number;
}

export const BarChart: React.FC<{ data: BarDatum[]; color?: string }> = ({
  data,
  color = '#7c3aed',
}) => {
  if (data.length === 0) return <EmptyChart />;

  const max = niceMax(Math.max(...data.map((d) => d.value)));
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slot = plotW / data.length;
  const barW = Math.min(26, slot * 0.55);
  const ticks = [0, max / 2, max];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="none">
      {ticks.map((t) => {
        const y = PAD.top + plotH - (t / max) * plotH;
        return (
          <g key={t}>
            <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#EDEDF2" strokeWidth={1} />
            <text x={PAD.left - 7} y={y + 3} textAnchor="end" fontSize={8} fill="#9A9AAA">
              {Math.round(t)}
            </text>
          </g>
        );
      })}

      {data.map((d, i) => {
        const h = max === 0 ? 0 : (d.value / max) * plotH;
        const x = PAD.left + slot * i + (slot - barW) / 2;
        const y = PAD.top + plotH - h;
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={barW} height={Math.max(h, 2)} rx={4} fill={color} opacity={0.9}>
              <title>{`${d.label}: ${d.value}`}</title>
            </rect>
            <text
              x={x + barW / 2}
              y={H - PAD.bottom + 13}
              textAnchor="middle"
              fontSize={8}
              fill="#9A9AAA"
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

interface LineSeries {
  label: string;
  color: string;
  points: { label: string; value: number }[];
}

export const LineChart: React.FC<{ series: LineSeries[] }> = ({ series }) => {
  const allPoints = series.flatMap((s) => s.points);
  if (allPoints.length === 0) return <EmptyChart />;

  const values = allPoints.map((p) => p.value);
  const rawMax = Math.max(...values);
  const rawMin = Math.min(...values);
  // Pad the band so a flat-ish line doesn't hug the top or bottom edge
  const max = Math.min(100, Math.ceil((rawMax + 5) / 5) * 5);
  const min = Math.max(0, Math.floor((rawMin - 5) / 5) * 5);
  const span = max - min || 1;

  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const count = Math.max(...series.map((s) => s.points.length));
  const xAt = (i: number) => PAD.left + (count === 1 ? plotW / 2 : (plotW / (count - 1)) * i);
  const yAt = (v: number) => PAD.top + plotH - ((v - min) / span) * plotH;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="none">
      {[min, (min + max) / 2, max].map((t) => (
        <g key={t}>
          <line
            x1={PAD.left}
            y1={yAt(t)}
            x2={W - PAD.right}
            y2={yAt(t)}
            stroke="#EDEDF2"
            strokeWidth={1}
          />
          <text x={PAD.left - 7} y={yAt(t) + 3} textAnchor="end" fontSize={8} fill="#9A9AAA">
            {Math.round(t)}
          </text>
        </g>
      ))}

      {series.map((s) => {
        const path = s.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.value)}`).join(' ');
        return (
          <g key={s.label}>
            <path d={path} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            {s.points.map((p, i) => (
              <circle key={p.label + i} cx={xAt(i)} cy={yAt(p.value)} r={2.75} fill="#fff" stroke={s.color} strokeWidth={2}>
                <title>{`${s.label} — ${p.label}: ${p.value}`}</title>
              </circle>
            ))}
          </g>
        );
      })}

      {series[0]?.points.map((p, i) => (
        <text
          key={p.label + i}
          x={xAt(i)}
          y={H - PAD.bottom + 13}
          textAnchor="middle"
          fontSize={8}
          fill="#9A9AAA"
        >
          {p.label}
        </text>
      ))}
    </svg>
  );
};

const EmptyChart = () => (
  <div className="flex h-full items-center justify-center text-sm text-canvas-500">
    No data yet
  </div>
);

export const ChartLegend: React.FC<{ items: { label: string; color: string }[] }> = ({ items }) => (
  <div className="flex items-center gap-4">
    {items.map((item) => (
      <span key={item.label} className="inline-flex items-center gap-1.5 text-xs text-canvas-600">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
        {item.label}
      </span>
    ))}
  </div>
);
